"use client";

import { useLayoutEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export function useHeroAnimation({
  containerRef,
  stageRef,
  frameRef,
  scanRef,
  onProgress,
}) {
  useLayoutEffect(() => {
    const container = containerRef.current;
    const stage = stageRef.current;
    const frame = frameRef.current;

    if (!container || !stage || !frame) return undefined;

    const audio = new Audio("/beep.mp3");
    audio.preload = "auto";
    let previousProgress = 0;

    const context = gsap.context(() => {
      gsap.set(".barcode", { top: 10, right: -220 });
      gsap.set(".ingredient", { left: -32 });
      gsap.set(".effects", { right: "-100%" });
      gsap.set(".toptext", { top: -50 });
      gsap.set(".note", { bottom: "-100%" });
      gsap.set(".number1", { left: "-100%" });
      gsap.set(scanRef.current, { autoAlpha: 0, yPercent: -130 });

      const timeline = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: container,
          start: "top top",
          end: () => `+=${Math.round(window.innerHeight * 1.65)}`,
          pin: stage,
          pinSpacing: true,
          scrub: 0.28,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          fastScrollEnd: true,
          onUpdate: (self) => {
            const progress = self.progress;
            onProgress(progress);

            if (progress >= 0.72 && previousProgress < 0.72 && self.direction > 0) {
              audio.currentTime = 0;
              audio.play().catch(() => {});
            }

            previousProgress = progress;
          },
          onRefresh: (self) => onProgress(self.progress),
        },
      });

      timeline
        .to(".barcode", { right: 0, duration: 0.42 }, 0.08)
        .to(".ingredient", { left: 8, duration: 0.42 }, 0.08)
        .to(".effects", { right: 15, duration: 0.42 }, 0.08)
        .to(".toptext", { top: 5, duration: 0.42 }, 0.08)
        .to(".note", { bottom: 15, duration: 0.42 }, 0.08)
        .to(".number1", { left: 15, duration: 0.42 }, 0.08)
        .to(scanRef.current, { autoAlpha: 0.82, yPercent: 120, duration: 0.08 }, 0.67)
        .to(scanRef.current, { yPercent: 690, autoAlpha: 0, duration: 0.14 }, 0.75)
        .to(frame, {
          clipPath: "inset(50% 0 50% 0)",
          autoAlpha: 0,
          duration: 0.14,
          ease: "power2.inOut",
        }, 0.86)
        .to(stage.querySelector("canvas"), {
          clipPath: "inset(50% 0 50% 0)",
          autoAlpha: 0,
          duration: 0.14,
          ease: "power2.inOut",
        }, 0.86);

      onProgress(0);
    }, container);

    const refreshId = window.requestAnimationFrame(() => ScrollTrigger.refresh());

    return () => {
      window.cancelAnimationFrame(refreshId);
      audio.pause();
      context.revert();
    };
  }, [containerRef, frameRef, onProgress, scanRef, stageRef]);
}
